import { api } from '../services/apiClient';

/**
 * Command resolution.
 *
 * This is deliberately deterministic — no model. A command bar has to be
 * instant and predictable: the same keystrokes must produce the same result
 * every time, and it must work with no AI credentials configured. An LLM in
 * this path would add 800ms of latency and a failure mode to the fastest
 * interaction in the product.
 *
 * `Resolver` is an interface so an AI resolver can be added ALONGSIDE this one
 * later (answering "which customers slipped last quarter" — questions this
 * cannot parse) without the UI changing. Deterministic results stay first;
 * the model handles the tail.
 */

export type CommandKind = 'navigate' | 'create' | 'record' | 'action' | 'ai';

export interface Command {
  id: string;
  kind: CommandKind;
  label: string;
  /** Secondary line — entity code, module, or context. */
  hint?: string;
  group: string;
  /** Route to push, for navigate/record. */
  path?: string;
  keywords?: string[];
  /** Ranking score, higher first. Filled by the matcher. */
  score?: number;
}

export interface Resolver {
  name: string;
  resolve(query: string, signal?: AbortSignal): Promise<Command[]>;
}

/** Static commands: every module, and every create action that exists. */
const STATIC_COMMANDS: Command[] = [
  // Navigation
  { id: 'nav.dashboard', kind: 'navigate', group: 'Go to', label: 'Dashboard', path: '/dashboard', keywords: ['home', 'overview'] },
  { id: 'nav.finance', kind: 'navigate', group: 'Go to', label: 'Finance', path: '/finance', keywords: ['accounts', 'ledger'] },
  { id: 'nav.coa', kind: 'navigate', group: 'Go to', label: 'Chart of Accounts', path: '/finance/accounts', keywords: ['coa'] },
  { id: 'nav.journal', kind: 'navigate', group: 'Go to', label: 'Journal Entries', path: '/finance/journal' },
  { id: 'nav.ledger', kind: 'navigate', group: 'Go to', label: 'General Ledger', path: '/finance/ledger' },
  { id: 'nav.finreports', kind: 'navigate', group: 'Go to', label: 'Financial Reports', path: '/finance/reports', keywords: ['p&l', 'balance sheet'] },
  { id: 'nav.customers', kind: 'navigate', group: 'Go to', label: 'Customers', path: '/sales/customers', keywords: ['clients'] },
  { id: 'nav.invoices', kind: 'navigate', group: 'Go to', label: 'Invoices', path: '/sales/invoices', keywords: ['billing'] },
  { id: 'nav.quotations', kind: 'navigate', group: 'Go to', label: 'Quotations', path: '/sales/quotations', keywords: ['quotes'] },
  { id: 'nav.opportunities', kind: 'navigate', group: 'Go to', label: 'Opportunities', path: '/sales/opportunities', keywords: ['pipeline', 'deals'] },
  { id: 'nav.products', kind: 'navigate', group: 'Go to', label: 'Products', path: '/sales/products', keywords: ['items', 'catalogue'] },
  { id: 'nav.vendors', kind: 'navigate', group: 'Go to', label: 'Vendors', path: '/procurement/vendors', keywords: ['suppliers'] },
  { id: 'nav.pos', kind: 'navigate', group: 'Go to', label: 'Purchase Orders', path: '/procurement/orders', keywords: ['po'] },
  { id: 'nav.tds', kind: 'navigate', group: 'Go to', label: 'TDS Management', path: '/procurement/tds' },
  { id: 'nav.inventory', kind: 'navigate', group: 'Go to', label: 'Inventory', path: '/inventory', keywords: ['stock'] },
  { id: 'nav.warehouses', kind: 'navigate', group: 'Go to', label: 'Warehouses', path: '/inventory/warehouses' },
  { id: 'nav.projects', kind: 'navigate', group: 'Go to', label: 'Projects', path: '/projects' },
  { id: 'nav.tasks', kind: 'navigate', group: 'Go to', label: 'Tasks', path: '/projects/tasks' },
  { id: 'nav.compliance', kind: 'navigate', group: 'Go to', label: 'Compliance', path: '/compliance', keywords: ['gst', 'returns'] },
  { id: 'nav.settings', kind: 'navigate', group: 'Go to', label: 'Settings', path: '/settings' },

  // Create
  { id: 'new.invoice', kind: 'create', group: 'Create', label: 'New invoice', path: '/sales/invoices?new=1', keywords: ['bill', 'raise'] },
  { id: 'new.customer', kind: 'create', group: 'Create', label: 'New customer', path: '/sales/customers?new=1', keywords: ['add client'] },
  { id: 'new.quotation', kind: 'create', group: 'Create', label: 'New quotation', path: '/sales/quotations?new=1', keywords: ['quote'] },
  { id: 'new.vendor', kind: 'create', group: 'Create', label: 'New vendor', path: '/procurement/vendors?new=1', keywords: ['supplier'] },
  { id: 'new.po', kind: 'create', group: 'Create', label: 'New purchase order', path: '/procurement/orders?new=1', keywords: ['po', 'buy'] },
  { id: 'new.product', kind: 'create', group: 'Create', label: 'New product', path: '/sales/products?new=1', keywords: ['item'] },
  { id: 'new.project', kind: 'create', group: 'Create', label: 'New project', path: '/projects?new=1' },
  { id: 'new.journal', kind: 'create', group: 'Create', label: 'New journal entry', path: '/finance/journal?new=1' },
];

/**
 * Subsequence match with positional weighting.
 *
 * "ovinv" matches "Overdue Invoices"; a prefix hit outranks a mid-word hit, and
 * a word-boundary hit outranks a random-letter one. Simple, but it is what
 * makes a palette feel like it read your mind rather than ran a `LIKE`.
 */
export const fuzzyScore = (query: string, target: string): number => {
  const q = query.toLowerCase().trim();
  const t = target.toLowerCase();
  if (!q) return 0;
  if (t === q) return 1000;
  if (t.startsWith(q)) return 800 - t.length;
  if (t.includes(q)) return 600 - t.indexOf(q) - t.length * 0.1;

  let score = 0;
  let ti = 0;
  let streak = 0;
  for (const ch of q) {
    const found = t.indexOf(ch, ti);
    if (found === -1) return -1; // not a subsequence at all
    // Word starts are worth more than mid-word letters.
    const boundary = found === 0 || t[found - 1] === ' ' || t[found - 1] === '/';
    streak = found === ti ? streak + 1 : 0;
    score += (boundary ? 12 : 4) + streak * 3;
    ti = found + 1;
  }
  return score;
};

/** Static navigation and create commands. Instant, no network. */
export const staticResolver: Resolver = {
  name: 'static',
  async resolve(query: string) {
    const q = query.trim();
    if (!q) {
      // Empty query shows the common actions rather than nothing.
      return STATIC_COMMANDS.filter((c) => c.kind === 'create').slice(0, 6);
    }
    return STATIC_COMMANDS.map((c) => {
      const hay = [c.label, ...(c.keywords || [])];
      const score = Math.max(...hay.map((h) => fuzzyScore(q, h)));
      return { ...c, score };
    })
      .filter((c) => (c.score ?? -1) > 0)
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
      .slice(0, 8);
  },
};

/**
 * Live record search across customers, vendors, products and projects.
 *
 * Uses the AI tool endpoint's underlying data via the normal REST endpoints —
 * each is already tenant-scoped server-side, so this cannot reach another
 * workspace's records.
 */
export const recordResolver: Resolver = {
  name: 'records',
  async resolve(query: string, signal?: AbortSignal) {
    const q = query.trim();
    // Below three characters the result set is noise and the request is waste.
    if (q.length < 3) return [];

    const search = async (
      path: string,
      group: string,
      map: (row: any) => { label: string; hint?: string; path: string }
    ): Promise<Command[]> => {
      try {
        const res: any = await api.get(`${path}?search=${encodeURIComponent(q)}&limit=4`, { signal } as any);
        const rows: any[] =
          res?.customers ?? res?.vendors ?? res?.products ?? res?.projects ?? res?.data ?? [];
        return rows.slice(0, 4).map((row, i) => {
          const m = map(row);
          return {
            id: `${group}.${row.id ?? i}`,
            kind: 'record' as const,
            group,
            label: m.label,
            hint: m.hint,
            path: m.path,
            score: 500 - i,
          };
        });
      } catch {
        // A failing entity search must not take down the whole palette.
        return [];
      }
    };

    const results = await Promise.all([
      search('/sales/customers', 'Customers', (r) => ({
        label: r.company_name ?? r.name ?? 'Customer',
        hint: r.customer_code,
        path: `/sales/customers/${r.id}`,
      })),
      search('/procurement/vendors', 'Vendors', (r) => ({
        label: r.company_name ?? r.name ?? 'Vendor',
        hint: r.vendor_code,
        path: `/procurement/vendors/${r.id}`,
      })),
      search('/products', 'Products', (r) => ({
        label: r.product_name ?? r.name ?? 'Product',
        hint: r.product_code,
        path: `/sales/products/${r.id}`,
      })),
    ]);

    return results.flat();
  },
};

/** Run every resolver and merge, best first. */
export const resolveAll = async (query: string, resolvers: Resolver[], signal?: AbortSignal) => {
  const settled = await Promise.all(
    resolvers.map((r) => r.resolve(query, signal).catch(() => [] as Command[]))
  );
  return settled.flat().sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
};

export { STATIC_COMMANDS };
